import React, { createContext, ReactNode, useContext, useState } from 'react';
import { FinsightEntity } from 'shared';

interface ClipboardContextType {
  copiedEntities: FinsightEntity[];
  addToClipboard: (entity: FinsightEntity) => void;
  removeFromClipboard: (entityId: string) => void;
  clearClipboard: () => void;
  isInClipboard: (entityId: string) => boolean;
}

const ClipboardContext = createContext<ClipboardContextType | undefined>(
  undefined,
);

interface ClipboardProviderProps {
  children: ReactNode;
}

export const ClipboardProvider: React.FC<ClipboardProviderProps> = ({
  children,
}) => {
  const [copiedEntities, setCopiedEntities] = useState<FinsightEntity[]>([]);

  const addToClipboard = (entity: FinsightEntity) => {
    setCopiedEntities((prev) => {
      // Check if entity is already in clipboard
      const exists = prev.some((e) => e.id === entity.id);
      if (exists) {
        return prev; // Don't add if already exists
      }
      return [...prev, entity];
    });
  };

  const removeFromClipboard = (entityId: string) => {
    setCopiedEntities((prev) =>
      prev.filter((entity) => entity.id !== entityId),
    );
  };

  const clearClipboard = () => {
    setCopiedEntities([]);
  };

  const isInClipboard = (entityId: string) => {
    return copiedEntities.some((entity) => entity.id === entityId);
  };

  const value: ClipboardContextType = {
    copiedEntities,
    addToClipboard,
    removeFromClipboard,
    clearClipboard,
    isInClipboard,
  };

  return (
    <ClipboardContext.Provider value={value}>
      {children}
    </ClipboardContext.Provider>
  );
};

export const useClipboard = (): ClipboardContextType => {
  const context = useContext(ClipboardContext);
  if (context === undefined) {
    throw new Error('useClipboard must be used within a ClipboardProvider');
  }
  return context;
};
